import { useState } from "react";
import { formatKstDay, formatKstStamp, normalizeSerial, normalizeTracking, toLegacyExcelRow } from "@rma-orbit/shared";
import type {
  ApiKeyCreated,
  Company,
  ExcelImportResponse,
  ExcelImportRow,
} from "@rma-orbit/shared";
import { api } from "../api/client.js";
import { navigate } from "../App.js";
import { copyRichText } from "../lib/clipboard.js";
import { Busy, ErrorNotice, Notice, useAsync } from "../lib/ui.js";
import { readFirstSheet } from "../lib/excel.js";

/**
 * 설정 (harness/UI-SPEC.md §1) — 협력사 직접 추가/수정 · 엑셀 이관.
 * 협력사는 **대표가 직접 추가한다.** 자동 생성은 없다 (DATA-DICTIONARY §1).
 */

/**
 * 이 파일은 관리 카드들의 모음이다. 화면은 각자 페이지가 됐다 (2026-09-09 대표 지시 —
 * 설정 탭 하나에 몰지 않는다): Companies · ImportExcel · Users · ApiKeys · System.
 */

/** 가입은 없다. 대표가 여기서만 계정을 만든다 (harness/SECURITY.md §1) */
export function UsersCard() {
  const list = useAsync(() => api.users(), []);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const created = await api.createUser({ email, name, password });
      setMessage(`${created.email} 계정을 만들었습니다.`);
      setEmail("");
      setName("");
      setPassword("");
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>
        사용자<span className="sub">가입은 없습니다 — 여기서만 계정을 만듭니다</span>
      </h2>

      {message && <Notice kind="ok">{message}</Notice>}
      <ErrorNotice error={error} />
      <ErrorNotice error={list.error} />

      <div className="row" style={{ marginBottom: 14 }}>
        <label className="field">
          이메일
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="field">
          이름
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          비밀번호 (8자 이상)
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button
          className="primary"
          disabled={busy || email === "" || name === "" || password.length < 8}
          onClick={create}
        >
          계정 만들기
        </button>
      </div>
      <p className="muted" style={{ marginTop: -6 }}>
        새 계정은 <strong>지금 로그인한 사람과 같은 범위</strong>를 봅니다. 자기보다 넓게 보는
        계정은 만들 수 없습니다.
      </p>

      {list.loading ? (
        <Busy />
      ) : (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>이메일</th>
                <th>이름</th>
                <th>상태</th>
                <th>만든 날</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.name}</td>
                  <td>
                    <span className="badge">{user.is_active ? "사용" : "정지"}</span>
                  </td>
                  <td className="num">{formatKstDay(user.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * 하네스 상태 (harness/HARNESS.md §6).
 * 돌린 적 없는 검사는 **PASS 로 채우지 않고** 없다고 말한다.
 */
export function HarnessCard() {
  const runs = useAsync(() => api.harness(), []);

  return (
    <div className="card">
      <h2>
        하네스 판정<span className="sub">검사별 가장 최근 1건</span>
      </h2>
      <ErrorNotice error={runs.error} />

      {runs.loading ? (
        <Busy />
      ) : (runs.data ?? []).length === 0 ? (
        <Notice kind="warn">
          아직 기록된 판정이 없습니다. 로컬에서 <code>pnpm harness:all</code> 을 돌리면 그 결과가
          여기에 남습니다 — <strong>돌린 적 없는 검사를 통과로 채우지는 않습니다.</strong>
        </Notice>
      ) : (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>검사</th>
                <th>판정</th>
                <th>통과</th>
                <th>실패</th>
                <th>건너뜀</th>
                <th>실행 시각</th>
              </tr>
            </thead>
            <tbody>
              {(runs.data ?? []).map((run) => (
                <tr key={run.harness}>
                  <td className="mono">{run.harness}</td>
                  <td>
                    <span
                      className="badge"
                      style={{
                        color:
                          run.verdict === "PASS"
                            ? "var(--ok)"
                            : run.verdict === "ISSUES_FOUND"
                              ? "var(--danger)"
                              : "var(--ink-3)",
                      }}
                    >
                      {run.verdict}
                    </span>
                  </td>
                  <td className="num">{run.passed}</td>
                  <td className="num">{run.failed}</td>
                  <td className="num">{run.skipped}</td>
                  <td className="num">{formatKstStamp(run.run_at, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** 감사 로그 — 누가 언제 무엇을 했는가. 본문(before/after)은 싣지 않는다 */
export function AuditCard() {
  const [entity, setEntity] = useState("");
  const log = useAsync(() => api.auditLog({ entity: entity || undefined, limit: 50 }), [entity]);

  return (
    <div className="card">
      <h2>
        감사 로그<span className="sub">누가 언제 무엇을 했는가 (최근 50건)</span>
      </h2>
      <ErrorNotice error={log.error} />

      <div className="row" style={{ marginBottom: 12 }}>
        <label className="field">
          대상
          <select value={entity} onChange={(e) => setEntity(e.target.value)}>
            <option value="">전체</option>
            <option value="users">사용자</option>
            <option value="companies">협력사</option>
            <option value="cases">케이스</option>
            <option value="approvals">승인</option>
            <option value="shipments">출고</option>
            <option value="intake_batches">입고 배치</option>
            <option value="device_notes">메모</option>
            {/*
              고를 수 없는 항목이 기록되고 있으면, 찾는 사람은 그것이 없다고 여긴다.
              실제로 남는 항목을 전부 연다 — 특히 교체(serial_swaps)는 이 프로젝트의 축이고,
              로그인(sessions)은 "누가 언제 들어왔나" 를 볼 유일한 자리다.
            */}
            <option value="serial_swaps">★ 교체 시리얼</option>
            <option value="evidence">증적</option>
            <option value="sessions">로그인</option>
            <option value="api_keys">API Key</option>
          </select>
        </label>
      </div>

      {log.loading ? (
        <Busy />
      ) : (
        <div className="tablewrap" style={{ maxHeight: 360, overflowY: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>시각</th>
                <th>행위</th>
                <th>대상</th>
                <th>대상 id</th>
                <th>사용자</th>
              </tr>
            </thead>
            <tbody>
              {(log.data?.items ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="num">{formatKstStamp(row.at, true)}</td>
                  <td className="mono">{row.action}</td>
                  <td>{row.entity}</td>
                  <td className="num">{row.entity_id ?? <span className="empty">—</span>}</td>
                  <td className="num">{row.user_id ?? <span className="empty">—</span>}</td>
                </tr>
              ))}
              {log.data?.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    기록이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function CompaniesCard({ canEdit }: { canEdit: boolean }) {
  const list = useAsync(() => api.companies(), []);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [kind, setKind] = useState<"PARTNER" | "VENDOR" | "SELF">("PARTNER");
  const [contact, setContact] = useState("");
  const [aliases, setAliases] = useState("");

  async function create() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const created = await api.createCompany({
        name,
        kind,
        contact: contact || undefined,
        aliases: aliases
          .split(",")
          .map((a) => a.trim())
          .filter((a) => a !== ""),
      });
      setMessage(`${created.name} 을(를) 추가했습니다.`);
      setName("");
      setContact("");
      setAliases("");
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function editAliases(company: Company) {
    const next = window.prompt(
      `${company.name} 의 별칭 (쉼표로 구분). 지우려면 빼고 저장하세요.`,
      company.aliases.join(", "),
    );
    if (next === null) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateCompany(company.id, {
        aliases: next
          .split(",")
          .map((a) => a.trim())
          .filter((a) => a !== ""),
      });
      setMessage(`${company.name} 의 별칭을 바꿨습니다.`);
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>
        협력사<span className="sub">엑셀의 여러 표기를 한 회사로 묶는 사전입니다</span>
      </h2>

      {message && <Notice kind="ok">{message}</Notice>}
      <ErrorNotice error={error} />
      <ErrorNotice error={list.error} />

      {canEdit && (
        <div className="row" style={{ marginBottom: 14 }}>
          <label className="field">
            이름
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            구분
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as "PARTNER" | "VENDOR" | "SELF")}
            >
              <option value="PARTNER">협력사</option>
              <option value="VENDOR">벤더(SECUI 등)</option>
              <option value="SELF">자사</option>
            </select>
          </label>
          <label className="field">
            연락처
            <input value={contact} onChange={(e) => setContact(e.target.value)} />
          </label>
          <label className="field grow">
            별칭 (쉼표로 구분)
            <input
              value={aliases}
              placeholder="트리즈MS, TrizMS"
              onChange={(e) => setAliases(e.target.value)}
            />
          </label>
          <button className="primary" disabled={busy || name.trim() === ""} onClick={create}>
            추가
          </button>
        </div>
      )}

      {list.loading ? (
        <Busy />
      ) : (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>이름</th>
                <th>구분</th>
                <th>별칭</th>
                <th>연락처</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((company) => (
                <tr key={company.id}>
                  <td>{company.name}</td>
                  <td>
                    <span className="badge">{company.kind}</span>
                  </td>
                  <td style={{ whiteSpace: "normal" }}>
                    {company.aliases.length === 0 ? (
                      <span className="empty">—</span>
                    ) : (
                      company.aliases.join(", ")
                    )}
                  </td>
                  <td>
                    {company.contact ?? <span className="empty">—</span>}
                  </td>
                  {canEdit && (
                    <td>
                      <button className="ghost" onClick={() => editAliases(company)}>
                        별칭 수정
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * 엑셀 이관 — 브라우저가 파싱·정규화하고, 서버는 JSON 행 + 원본 파일을 받는다
 * (harness/UI-SPEC.md §6, API-SPEC §11-1).
 */

export function ImportCard() {
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ExcelImportRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [report, setReport] = useState<ExcelImportResponse | null>(null);

  async function parse(chosen: File) {
    setBusy(true);
    setError(null);
    setReport(null);
    setRows(null);
    try {
      const sheet = await readFirstSheet(chosen);
      setRows(sheet.map(toLegacyExcelRow));
      setFile(chosen);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!file || !rows) return;
    setBusy(true);
    setError(null);
    try {
      setReport(
        await api.importExcel({ source_filename: file.name, rows }, file),
      );
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const badRows = (rows ?? []).filter(
    (r) => r.received_at === null || normalizeSerial(r.serial_raw).norm === "",
  );

  return (
    <div className="card">
      <h2>
        엑셀 이관<span className="sub">기존 방화벽.xlsx 를 그대로 올립니다</span>
      </h2>

      <ErrorNotice error={error} />

      <label
        className={`dropzone ${busy ? "busy" : ""}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const chosen = e.dataTransfer.files?.[0];
          if (chosen) void parse(chosen);
        }}
      >
        <input
          type="file"
          accept=".xlsx,.xls"
          hidden
          onChange={(e) => {
            const chosen = e.target.files?.[0];
            if (chosen) void parse(chosen);
          }}
        />
        <span className="dropzone-icon" aria-hidden="true">⇪</span>
        <strong>{busy ? "읽는 중…" : "방화벽.xlsx 를 여기에 끌어다 놓거나 눌러서 고르세요"}</strong>
        <span className="muted">.xlsx · .xls — 브라우저가 읽고, 서버가 되읽어 원본과 대조합니다</span>
      </label>

      {rows && (
        <>
          <Notice kind={badRows.length > 0 ? "warn" : "info"}>
            {rows.length}행을 읽었습니다.
            {badRows.length > 0 && (
              <>
                {" "}
                그중 {badRows.length}행은 입고일이나 시리얼을 읽지 못했습니다 — 그 줄은 이관되지
                않고 리포트에 남습니다. <strong>날짜를 대신 채우지 않습니다.</strong>
              </>
            )}
          </Notice>

          <div className="tablewrap" style={{ maxHeight: 320, overflowY: "auto", marginBottom: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>행</th>
                  <th>일자</th>
                  <th>업체</th>
                  <th>시리얼 → 저장값</th>
                  <th>송장 → 저장값</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 200).map((r) => (
                  <tr key={r.source_row_no}>
                    <td className="num">{r.source_row_no}</td>
                    <td className="num">
                      {r.received_at ?? <span className="empty">읽지 못함</span>}
                    </td>
                    <td>{r.company_raw}</td>
                    <td className="mono">
                      {r.serial_raw} →{" "}
                      {normalizeSerial(r.serial_raw).norm || <span className="empty">—</span>}
                    </td>
                    <td className="mono">
                      {r.tracking_raw ? (
                        <>
                          {r.tracking_raw} → {normalizeTracking(r.tracking_raw).norm}
                        </>
                      ) : (
                        <span className="empty">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row">
            <button className="primary" disabled={busy} onClick={send}>
              {busy ? "이관 중…" : `${rows.length}행 이관`}
            </button>
            <span className="muted">
              같은 (시리얼·입고일·업체)는 건너뜁니다 — 고쳐서 다시 올려도 두 배가 되지 않습니다.
            </span>
          </div>
        </>
      )}

      {report && (
        <div style={{ marginTop: 14 }}>
          <Notice kind={report.reconciliation.mismatches.length === 0 ? "ok" : "warn"}>
            <strong>
              원본 {report.reconciliation.source_rows}행 → 이관{" "}
              {report.reconciliation.imported_rows}건
            </strong>
            {report.reconciliation.mismatches.length === 0 ? (
              <> · 불일치 0 — 이관이 끝났습니다.</>
            ) : (
              <>
                {" "}
                · 불일치 {report.reconciliation.mismatches.length}건 —{" "}
                <strong>불일치가 0 이 될 때까지 이관은 완료가 아닙니다.</strong>
              </>
            )}
          </Notice>

          {report.reconciliation.mismatches.length > 0 && (
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>원본 행</th>
                    <th>항목</th>
                    <th>사유</th>
                  </tr>
                </thead>
                <tbody>
                  {report.reconciliation.mismatches.map((m, i) => (
                    <tr key={i}>
                      <td className="num">{m.source_row_no}</td>
                      <td className="mono">{m.field}</td>
                      <td style={{ whiteSpace: "normal" }}>{m.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * API Key (harness/SECURITY.md §4).
 *
 * 수집기가 쓰는 열쇠다. **평문은 발급 직후 한 번만 보인다** — 우리도 다시 볼 수 없다.
 * 그래서 화면이 "지금 복사하세요" 라고 분명히 말해야 한다. 나중에 찾을 수 있을 거라고
 * 생각하면 그 사람은 키를 잃는다.
 */
export function ApiKeyCard() {
  const list = useAsync(() => api.apiKeys(), []);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [issued, setIssued] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);

  async function create() {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      setIssued(await api.createApiKey(name.trim(), "ingest"));
      setName("");
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: number, keyName: string) {
    if (!window.confirm(`"${keyName}" 키를 폐기합니다. 그 키를 쓰는 수집기는 즉시 멈춥니다.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.revokeApiKey(id);
      list.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const items = list.data?.items ?? [];

  return (
    <div className="card">
      <h2>
        API Key<span className="sub">수집기가 쓰는 열쇠 — 사람 API 는 부를 수 없습니다</span>
      </h2>

      <ErrorNotice error={error} />
      <ErrorNotice error={list.error} />

      {issued && (
        <Notice kind="warn">
          <strong>지금 복사해 두세요. 이 값은 다시 볼 수 없습니다.</strong>
          <div className="row" style={{ marginTop: 8, alignItems: "center" }}>
            <input readOnly value={issued.key} className="mono grow" />
            <button
              onClick={() => {
                void navigator.clipboard.writeText(issued.key).then(() => setCopied(true));
              }}
            >
              복사
            </button>
            <button className="ghost" onClick={() => setIssued(null)}>
              닫기
            </button>
          </div>
          {copied && <div className="muted">복사했습니다.</div>}
          <div className="muted" style={{ marginTop: 6 }}>
            작업PC 의 <code>apps/collector/.env.local</code> 에 넣으세요. 저장소·D1·R2 어디에도
            넣지 않습니다.
          </div>
        </Notice>
      )}

      <div className="row" style={{ marginBottom: 12 }}>
        <label className="field grow">
          이름 (어디에 쓰는 키인지)
          <input
            value={name}
            placeholder="예: 작업PC 수집기"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <button className="primary" disabled={busy || name.trim().length < 2} onClick={create}>
          ingest 키 발급
        </button>
      </div>

      {list.loading ? (
        <Busy />
      ) : items.length === 0 ? (
        <p className="muted" style={{ marginBottom: 0 }}>
          아직 없습니다. 수집기를 붙일 때 하나 만들면 됩니다.
        </p>
      ) : (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>이름</th>
                <th>스코프</th>
                <th>발급</th>
                <th>마지막 사용</th>
                <th>상태</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td className="mono">{k.scope}</td>
                  <td className="num">{formatKstDay(k.created_at)}</td>
                  <td className="num">
                    {k.last_used_at ? formatKstStamp(k.last_used_at) : <span className="empty">—</span>}
                  </td>
                  <td>
                    {k.revoked_at ? (
                      <span className="muted">폐기됨</span>
                    ) : (
                      <span className="badge">사용 중</span>
                    )}
                  </td>
                  <td>
                    {!k.revoked_at && (
                      <button className="ghost" disabled={busy} onClick={() => revoke(k.id, k.name)}>
                        폐기
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * SECUI 수집 상태 (harness/CRAWL-POLICY.md §1·§5).
 *
 * 여기서 **로그인을 대신해 주지 않는다.** 이 화면은 지금 어떤 상태인지 말하고,
 * 사람이 무엇을 해야 하는지 한 줄로 알려 준다. 실제 로그인은 작업PC 에서 수집기가
 * 창을 띄우고, 대표가 SMS 인증번호를 직접 친다 — 계정과 세션은 이 PC 밖으로 나가지 않는다.
 */
export function CrawlCard() {
  const status = useAsync(() => api.crawlStatus(), []);
  const s = status.data;

  return (
    <div className="card">
      <h2>
        SECUI 수집<span className="sub">하루 한 번 · 교체 시리얼을 자동으로 잇습니다</span>
      </h2>
      <ErrorNotice error={status.error} />

      {status.loading ? (
        <Busy />
      ) : s === undefined || s === null ? null : s.never_ran ? (
        <Notice kind="warn">
          <strong>아직 한 번도 돌지 않았습니다.</strong> 작업PC 에서 아래 순서로 시작합니다 —
          한 번 로그인해 두면 세션이 살아 있는 동안은 사람 손이 필요 없습니다.
        </Notice>
      ) : s.needs_login ? (
        <Notice kind="error">
          <strong>SECUI 세션이 끊겼습니다.</strong> 수집이 멈춰 있습니다 — 아래 명령으로 다시
          로그인해 주세요(1분). 인증번호는 직접 입력해야 합니다.
        </Notice>
      ) : s.stale ? (
        <Notice kind="warn">
          <strong>마지막 성공이 26시간을 넘었습니다.</strong> 하루 한 번 도는 일이 두 번
          걸렀다는 뜻입니다 — 아래 마지막 판정을 봐 주세요.
        </Notice>
      ) : (
        <Notice kind="ok">
          <strong>정상입니다.</strong> 마지막 성공 {formatKstStamp(s.last_ok_at)}.
        </Notice>
      )}

      {s?.last_run && (
        <div className="tablewrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>마지막 실행</th>
                <th>판정</th>
                <th className="num">본 건</th>
                <th className="num">새 건</th>
                <th className="num">교체 연결</th>
                <th>메모</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="num">{formatKstStamp(s.last_run.started_at, true)}</td>
                <td>
                  <span
                    className="badge"
                    style={{
                      color: s.last_run.verdict === "OK" ? "var(--ok)" : "var(--danger)",
                    }}
                  >
                    {s.last_run.verdict ?? "—"}
                  </span>
                </td>
                <td className="num">{s.last_run.cases_found ?? <span className="empty">—</span>}</td>
                <td className="num">{s.last_run.cases_new ?? <span className="empty">—</span>}</td>
                <td className="num">
                  {s.last_run.swaps_matched ?? <span className="empty">—</span>}
                </td>
                <td>{s.last_run.error ?? <span className="empty">—</span>}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <div className="muted" style={{ marginBottom: 6 }}>
          작업PC 에서 (계정과 세션은 이 PC 밖으로 나가지 않습니다)
        </div>
        <p style={{ margin: "0 0 6px" }}>
          <code>apps/collector/start-collector.bat</code> 을 더블클릭해 켜 두고,{" "}
          <a href="#/collect" onClick={(e) => { e.preventDefault(); navigate("#/collect"); }}>
            [SECUI 수집]
          </a>{" "}
          화면의 버튼으로 로그인·수집·자동 수집을 켭니다. 터미널을 볼 일은 없습니다.
        </p>
        <pre className="mono" style={{ margin: 0 }}>
{`# 터미널을 쓰고 싶으면 (같은 일):
cd apps/collector
python -m collector signin           # 창이 뜹니다 → SMS 인증번호를 직접 입력
python -m collector crawl --capture  # 지금 한 번 수집 (원문도 저장)
python -m collector serve            # 사이트 화면이 부를 수 있게 켜 둡니다`}
        </pre>
      </div>
    </div>
  );
}

/**
 * MCP 서버 (harness/KNOWLEDGE-MAP.md §2 — `apps/mcp`).
 *
 * Claude Desktop / Claude Code 가 이 사이트의 지식 API 를 도구로 부르게 하는 설정 조각을
 * 보여 준다. 키는 여기서 만들지 않는다 — 위 API Key 카드에서 `read` 키를 발급해
 * 붙여넣는다. 키를 화면이 대신 채워 주면 화면 소스에 키가 남는다.
 */
export function McpCard() {
  const [copied, setCopied] = useState(false);
  const base = window.location.origin;
  const repo = "D:/trizms-rma-orbit"; // 작업PC 기준 예시 — 다른 PC 면 경로만 바꾼다
  const snippet = `{
  "mcpServers": {
    "rma-orbit": {
      "command": "node",
      "args": ["${repo}/apps/mcp/server.mjs"],
      "env": { "ORBIT_BASE": "${base}", "ORBIT_READ_KEY": "orbit_여기에_read_키" }
    }
  }
}`;

  return (
    <div className="card">
      <h2>
        MCP 서버<span className="sub">에이전트가 이 사이트를 도구로 씁니다 — 읽기 전용</span>
      </h2>
      <p style={{ margin: "0 0 8px" }}>
        <code>apps/mcp/server.mjs</code> 는 의존성 없는 stdio MCP 서버입니다. 도구 7개(목차·규칙·협력사·케이스·교체·장비·
        매니페스트)를 내고, 전부 <strong>읽기</strong>만 합니다. 쓰기는 사람이 화면에서 합니다.
      </p>
      <ol style={{ margin: "0 0 8px", paddingLeft: 18 }}>
        <li>위 API Key 카드에서 <span className="mono">read</span> 키를 발급합니다 (발급한 사람의 스코프를 물려받습니다).</li>
        <li>아래 조각을 Claude Desktop 의 <code>claude_desktop_config.json</code> 또는 Claude Code 의{" "}
          <code>.mcp.json</code> 에 붙여넣고 키를 채웁니다.</li>
        <li>왕복 확인: <span className="mono">node apps/mcp/smoke.mjs</span> (사이트가 떠 있어야 합니다)</li>
      </ol>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <pre className="mono grow" style={{ margin: 0, padding: "8px 10px", background: "var(--bg)", borderRadius: 8, overflowX: "auto" }}>
          {snippet}
        </pre>
        <button
          onClick={() => {
            void copyRichText(`<pre>${snippet}</pre>`, snippet).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? "복사됨" : "복사"}
        </button>
      </div>
      <p className="muted" style={{ margin: "8px 0 0", fontSize: 12 }}>
        구조 전체는{" "}
        <a href="#/map" onClick={(e) => { e.preventDefault(); navigate("#/map"); }}>
          지식 맵
        </a>{" "}
        · 진입점 <a href="/rma/manifest.json" target="_blank" rel="noreferrer" className="mono">/rma/manifest.json</a>
      </p>
    </div>
  );
}
